import { DataFilterField, DataFilterOption, DataFilterValues } from '../../shared/ui/data-filter/data-filter.component';
import { LookupApiService } from '../../services/lookup-api.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MapNavigationService } from '../../map/core/map-navigation.service';
import { hasMapCoordinates } from '../../map/core/spatial/map-viewport';
import { HttpErrorResponse } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { Component, DestroyRef, ElementRef, Inject, inject, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { ActiveElement, Chart, ChartData, ChartOptions, LegendItem } from 'chart.js';
import { EMPTY, filter, finalize, startWith, takeUntil, Subscription, map, of, switchMap, catchError, firstValueFrom, throwError } from 'rxjs';
import { ConfirmationDialogService } from '../../shared/ui/confirmation-dialog/confirmation-dialog.service';
import { AppContextService } from '../../services/app-context.service';
import { CompanyProfileService } from '../../configuration/account/company-profile.service';
import * as demo from './overview-model';
import { OverviewApiService, OverviewPeriod, OverviewReport, OverviewAnalysisListItem, OverviewAnalysisQuery, OverviewTemplate, SavedAnalysis } from './overview-api.service';
import { DataListComponent, DataListSortOption } from '../../shared/ui/data-list/data-list.component';
import { DataFetch } from '../../models/data-source';
import { TenantContextService } from '../../services/tenant-context.service';
import { overviewDetailTitles, OverviewView as View } from './overview-routing';
import { rootBreadcrumbQueryParams } from '../../components/workspace/workspace-breadcrumbs.component';
import { WorkspaceNavigationService } from '../../services/workspace-navigation.service';
import { CommandConversationService, OverviewAnalysisPublished } from '../../services/command-conversation.service';

interface StatementLine { label: string; key: keyof demo.Amounts; headline?: boolean }
interface StatementRow extends DetailRow {
  key: keyof demo.Amounts; headline?: boolean;
  margin?: { current: number; prior: number };
}
interface StatementSection { name: string; view: View; rows: StatementRow[] }
const incomeLines: StatementLine[] = [
  { label: 'Revenue', key: 'revenue', headline: true },
  { label: 'Service costs', key: 'serviceCost' },
  { label: 'Operating expenses', key: 'overhead' },
  { label: 'Operating profit', key: 'profit', headline: true },
  { label: 'Interest expense', key: 'interest' },
  { label: 'Income tax', key: 'tax' },
  { label: 'Net income', key: 'netIncome', headline: true },
];
const cashLines: StatementLine[] = [
  { label: 'Operating cash flow', key: 'operating', headline: true },
  { label: 'Investing cash flow', key: 'investing' },
  { label: 'Financing cash flow', key: 'financing' },
  { label: 'Net cash movement', key: 'net' },
];
interface DetailRow { label: string; current: number; prior: number }
type TrendMetric = 'revenue' | 'profit' | 'operating';
/** A listed report carries its full Markdown document, so each card renders without a request per row. */
type ReportListItem = OverviewAnalysisListItem;

/** Company Overview renders immutable backend reports and saved private report outputs. */
@Component({
  selector: 'app-home-view',
  host: { class: 'workspace-page--full' },
  templateUrl: './home-view.component.html',
  styleUrls: ['./home-view.component.css'],
})
export class HomeViewComponent implements OnInit, OnDestroy {
  @ViewChild('body', { static: true }) body!: ElementRef<HTMLElement>;
  /** Reporting intervals come from the system lookup; the backend validates every code it receives. */
  intervalOptions: DataFilterOption[] = [];
  readonly money = demo.money;
  readonly signedMoney = demo.signedMoney;
  readonly pp = demo.percentagePoints;
  readonly percent = (value: number) => Number.isFinite(value) ? value.toFixed(1) + '%' : 'No comparable baseline';
  private readonly ratio = (value: number, base: number) => base > 0 ? 100 * value / base : NaN;
  readonly growth = demo.growth;
  /** Analysis & Reports uses the shared data list: its filter bar, search, Sort by and Load more. */
  @ViewChild('reportList') reportList?: DataListComponent<ReportListItem>;
  reportListFilters: DataFilterValues = {};
  reportFilterFields: DataFilterField[] = [];
  reportSearch = '';
  /** Updated is the server's default order; Period and Template are applied by the list endpoint. */
  readonly reportSortOptions: readonly DataListSortOption[] = [
    { label: 'Updated', field: 'updated', desc: true }, { label: 'Period', field: 'period', desc: true }, { label: 'Template', field: 'template' },
  ];
  private periodFilterOptions: DataFilterOption[] = [];
  private periodFilterRequest?: Subscription;
  readonly fetchReports: DataFetch<ReportListItem> = query => {
    const offset = (query.page - 1) * query.pageSize;
    const interval = query.filters['interval'] as demo.Interval | undefined;
    const filter: OverviewAnalysisQuery = { offset, pageSize: query.pageSize, ...(interval ? { interval } : {}),
      ...(interval && query.filters['period'] ? { periodStart: query.filters['period'] as string } : {}),
      ...(query.search.trim() ? { keyword: query.search.trim() } : {}), ...(query.sort ? { sort: query.sort, desc: !!query.desc } : {}) };
    // The list endpoint returns each report's full content and the total, so cards need no further requests.
    return this.api.analysisList(filter).pipe(map(page => {
      this.settlePublished(page.items, query.page === 1);
      return page;
    }));
  };
  /** Reports published since the list last loaded; the first page that answers decides whether the filters hide them. */
  private pendingPublished: OverviewAnalysisPublished[] = [];
  private settlePublished(items: OverviewAnalysisListItem[], firstPage: boolean): void {
    const shown = new Set(items.map(item => item.id));
    this.hiddenPublished = this.hiddenPublished.filter(item => !shown.has(item.id));
    if (!firstPage || !this.pendingPublished.length) return;
    const hidden = this.reportQueryActive ? this.pendingPublished.filter(item => !shown.has(item.id)) : [];
    this.hiddenPublished = [...this.hiddenPublished.filter(item => !hidden.some(h => h.id === item.id)), ...hidden];
    this.pendingPublished = [];
  }
  /** Period choices follow the selected interval and list only periods with financial data. */
  changeReportFilters(values: DataFilterValues): void {
    const interval = values['interval'] as demo.Interval | undefined;
    const intervalChanged = interval !== this.reportListFilters['interval'];
    this.reportListFilters = intervalChanged ? { interval: interval ?? null } : values;
    if (intervalChanged) this.loadPeriodFilterOptions(interval);
  }
  private loadPeriodFilterOptions(interval?: demo.Interval): void {
    this.periodFilterRequest?.unsubscribe(); this.periodFilterOptions = []; this.buildReportFilterFields();
    if (!interval) return;
    this.periodFilterRequest = this.api.report({ interval, comparison: 'year' }).pipe(takeUntil(this.tenant.sessionChanged$)).subscribe({
      next: report => { this.periodFilterOptions = report.periods.map(p => ({ value: p.periodStart, label: (interval === 'weekly' ? 'Week of ' : '') + demo.period(p.periodStart, interval).label })); this.buildReportFilterFields(); },
      error: () => this.buildReportFilterFields(),
    });
  }
  private buildReportFilterFields(): void {
    this.reportFilterFields = [
      { id: 'interval', label: 'Interval', allLabel: 'All intervals', options: this.intervalOptions },
      ...(this.reportListFilters['interval'] ? [{ id: 'period', label: 'Period', allLabel: 'All periods', options: this.periodFilterOptions }] : []),
    ];
  }
  get reportQueryActive(): boolean { return !!this.reportListFilters['interval'] || !!this.reportSearch.trim(); }
  readonly reportPeriod = demo.reportPeriodLabel;
  readonly trackReport = (_index: number, item: OverviewAnalysisListItem) => item.id;
  /**
   * Marks a preview clipped when its full content is taller than the preview height, which shows the fade and
   * Continue reading. Both the content and its clipping box are watched, so a taller workspace re-checks too.
   */
  private readonly previewObserver = new ResizeObserver(entries => {
    for (const entry of entries) {
      const box = entry.target.classList.contains('overview-report-preview__content') ? entry.target : entry.target.parentElement;
      const content = box?.firstElementChild;
      if (!box?.parentElement || !content) continue;
      box.parentElement.classList.toggle('is-clipped', content.scrollHeight > box.clientHeight + 1);
    }
  });
  @ViewChildren('reportPreview') set reportPreviews(items: QueryList<ElementRef<HTMLElement>>) {
    this.previewObserver.disconnect();
    items.forEach(item => {
      this.previewObserver.observe(item.nativeElement);
      if (item.nativeElement.parentElement) this.previewObserver.observe(item.nativeElement.parentElement);
    });
  }
  /** A report preview shows in full up to 80% of the workspace content height. */
  private readonly bodyObserver = new ResizeObserver(([entry]) => entry && (entry.target as HTMLElement).style
    .setProperty('--overview-preview-max', Math.round(entry.contentRect.height * 0.8) + 'px'));
  /** Reports published while Overview is open that the current list filters do not show; filters stay as chosen. */
  hiddenPublished: OverviewAnalysisPublished[] = [];
  openPublished(published: OverviewAnalysisPublished): void {
    this.hiddenPublished = this.hiddenPublished.filter(item => item.id !== published.id);
    void this.router.navigate(['/overview', 'reports', published.id], { queryParams: this.reportingParams(), state: this.navigation.sourceState('Business Insight') });
  }
  dismissPublished(): void { this.hiddenPublished = []; }
  openReport(item: OverviewAnalysisListItem): void {
    void this.router.navigate(['/overview', 'reports', item.id], {
      queryParams: { ...this.reportingParams(), interval: item.interval, period: item.periodStart }, state: this.navigation.sourceState('Business Insight'),
    });
  }
  report?: OverviewReport;
  catalog: OverviewPeriod[] = [];
  loading = true;
  error = '';
  analysisError = '';
  profileError = '';
  removing = false;
  private pendingScroll?: number;
  private scrollTimer?: ReturnType<typeof setTimeout>;
  selectedReport?: SavedAnalysis;
  /** Template of the open report, named by the analysis read itself, for the header and Generate another period. */
  findingType?: Pick<OverviewTemplate, 'id' | 'name'>;
  private loadSubscription?: Subscription;
  readonly moduleTitles: Record<View, string> = { overview: 'Company performance', ...overviewDetailTitles,
    cash: 'Cash & financial position' };
  readonly trendMetrics: { key: TrendMetric; label: string }[] = [
    { key: 'revenue', label: 'Revenue' }, { key: 'profit', label: 'Operating profit' }, { key: 'operating', label: 'Operating cash flow' },
  ];
  trendMetric: TrendMetric = 'revenue';
  interval: demo.Interval = demo.defaultInterval;
  start = '';
  comparison: demo.Comparison = 'year';
  view: View = 'overview';
  periodOptions: demo.Period[] = [];
  snapshot!: demo.Snapshot;
  statements: StatementSection[] = [];
  balances: DetailRow[] = [];
  details: DetailRow[] = [];
  marginAxisNote = '';
  trend: ChartData<'line'> = { datasets: [] };
  detailOptions: ChartOptions<'bar'> = {};
  trendOptions: ChartOptions<'line'> = {};
  trendDescription = '';
  profitTrend: ChartData<'line'> = { datasets: [] };
  profitTrendOptions: ChartOptions<'line'> = {};
  cashChart: ChartData<'bar'> = { datasets: [] };
  private subscriptions = new Subscription();
  private initialized = false;
  private trendPeriods: demo.Period[] = [];
  private focusTimer?: ReturnType<typeof setTimeout>;
  private color = (name: string) => getComputedStyle(this.document.documentElement).getPropertyValue(`--br-color-${name}`).trim();
  // Legend swatches distinguish actual and comparison series by their line dash.
  private readonly lineLegend = (chart: Chart): LegendItem[] => Chart.defaults.plugins.legend.labels.generateLabels(chart)
    .map(item => {
      // Point-style defaults describe the point, not the dataset's line dash.
      const dash = (chart.data.datasets as ChartData<'line'>['datasets'])[item.datasetIndex ?? 0].borderDash;
      return { ...item, lineDash: Array.isArray(dash) ? dash.filter((value): value is number => typeof value === 'number') : [], pointStyle: 'line' as const };
    });

  constructor(readonly companyProfile: CompanyProfileService, private readonly route: ActivatedRoute, private readonly router: Router,
    private readonly context: AppContextService, @Inject(DOCUMENT) private readonly document: Document,
    private readonly navigation: WorkspaceNavigationService, private readonly conversation: CommandConversationService, private readonly api: OverviewApiService,
    private readonly tenant: TenantContextService, private readonly mapNavigation: MapNavigationService,
    private readonly lookups: LookupApiService, private readonly confirmation: ConfirmationDialogService) {}

  private readonly destroyRef = inject(DestroyRef);
  locatingCompany = false;
  companyLocationError = '';
  async navigateToCompany(): Promise<void> {
    if (this.locatingCompany || !this.companyProfile.address()) return;
    this.locatingCompany = true; this.companyLocationError = '';
    const sessionVersion = this.tenant.sessionVersion;
    try {
      const p = this.companyProfile.profile();
      const location = hasMapCoordinates(p.latitude, p.longitude)
        ? { latitude:p.latitude!, longitude:p.longitude! }
        : await firstValueFrom(this.companyProfile.location().pipe(takeUntilDestroyed(this.destroyRef)));
      if (this.tenant.sessionVersion !== sessionVersion) return;
      if (!hasMapCoordinates(location.latitude, location.longitude)) throw new Error('Company address could not be located.');
      if (await this.router.navigateByUrl('/') && this.tenant.sessionVersion === sessionVersion)
        this.mapNavigation.navigateTo({ ...location, zoom:17 });
    } catch (error) {
      if (this.tenant.sessionVersion === sessionVersion)
        this.companyLocationError = error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message : 'Company address could not be located.';
    } finally { this.locatingCompany = false; }
  }

  readonly trackName = (_index: number, value: { name: string }) => value.name;
  loadReport(force = true): void {
    this.loadSubscription?.unsubscribe();
    const cached = this.report;
    this.error = ''; this.analysisError = '';
    const analysisId = this.route.snapshot.firstChild?.paramMap?.get('analysisId');
    // The open detail carries its type name; sibling list rows do not.
    const selectedCached = this.selectedReport?.id === analysisId ? this.selectedReport : undefined;
    const reuse = !force && cached && (this.view === 'analysis' ? selectedCached?.reportId === cached.id
      : cached.interval === this.interval && cached.comparison === this.comparison && (!this.start || cached.periodStart === this.start));
    if (!reuse) { this.report = undefined; this.selectedReport = undefined; this.findingType = undefined; }
    this.loading = !reuse;
    // A saved report's panel does not depend on the Overview comparison; if its financial report cannot be read
    // with the current one (no earlier period stored), read it with the other rather than refuse to open the report.
    const savedReport = (reportId: string) => this.api.report({ reportId, comparison: this.comparison }).pipe(catchError(error =>
      this.api.report({ reportId, comparison: this.comparison === 'year' ? 'previous' : 'year' }).pipe(catchError(() => throwError(() => error)))));
    // Interval, period and comparison are filters on the one report read; a saved analysis reads its own report through its foreign key.
    const selection = reuse ? of({ report: cached!, selected: this.view === 'analysis' ? selectedCached : undefined })
      : this.view === 'analysis' && analysisId
      ? this.api.analysis(analysisId).pipe(switchMap(selected =>
        savedReport(selected.reportId).pipe(map(report => ({ report, selected: selected as SavedAnalysis | undefined })))))
      : this.api.report({ interval: this.interval, period: this.start || undefined, comparison: this.comparison })
        .pipe(map(report => ({ report, selected: undefined as SavedAnalysis | undefined })));
    this.loadSubscription = selection.subscribe({
      next: result => {
        this.loading = false;
        const { report, selected } = result;
        this.report = report; this.catalog = report.periods;
        // The type labels the detail and preselects Generate another period; an unavailable type leaves it unlabeled.
        this.findingType = selected?.templateId && selected.templateName ? { id: selected.templateId, name: selected.templateName } : undefined;
        this.interval = report.interval; this.comparison = report.comparison; this.start = report.periodStart;
        this.periodOptions = this.catalog.map(item => demo.period(item.periodStart, item.interval));
        this.snapshot = report.data.snapshot;
        this.refresh();
        this.selectedReport = selected;
        this.restoreScroll();
      },
      error: (error: unknown) => {
        this.loading = false;
        this.error = error instanceof HttpErrorResponse && typeof error.error?.message === 'string' ? error.error.message
          : this.view === 'analysis' ? 'This analysis is unavailable. Return to Business Insight or refresh to retry.' : 'Financial data could not be loaded. Check access and refresh to retry.';
      },
    });
  }
  private discussionRequest?: Subscription;
  /** Prepare a normal message on the server; the existing composer persists and sends it. */
  discussReport(source: SavedAnalysis): void {
    this.discussionRequest?.unsubscribe();
    this.analysisError = '';
    this.discussionRequest = this.api.discussion(source.id).pipe(
      takeUntil(this.tenant.sessionChanged$),
      takeUntil(this.router.events.pipe(filter(event => event instanceof NavigationEnd))),
    ).subscribe({
      next: draft => this.openAnalysisDraft(draft.message, draft.title),
      error: () => { this.analysisError = 'The report discussion could not be prepared. Please try again.'; },
    });
  }
  get findingPeriod(): string {
    return this.report ? (this.report.interval === 'weekly' ? 'Week of ' : '') + this.snapshot.period.label : '';
  }
  openTemplates(): void { void this.router.navigate(['/overview/templates']); }
  /** Opens this report's template, where Generate runs it for the latest period of the chosen interval. */
  generateAnotherPeriod(): void {
    if (!this.report || !this.findingType) return;
    void this.router.navigate(['/overview/templates', this.findingType.id, 'generate'], { queryParams: { interval: this.report.interval } });
  }
  private openAnalysisDraft(draft: string, title: string): void {
    // The backend message contains the exact source, so same-title reports have distinct drafts.
    if (this.conversation.draft === draft) {
      this.conversation.selectConversation(this.conversation.activeConversation.id);
    } else {
      this.conversation.newConversation(); this.conversation.draft = draft;
      this.conversation.renameConversation(this.conversation.activeConversation.id, title);
    }
    this.conversation.open();
  }
  removeReport(source: SavedAnalysis): void {
    if (this.removing) return;
    this.removing = true;
    // Destructive actions confirm first, like deleting a campaign.
    this.subscriptions.add(this.confirmation.confirm({ title: 'Remove report?', message: `Remove “${source.title}”? This cannot be undone.`,
      confirmLabel: 'Remove', cancelLabel: 'Cancel', danger: true }).pipe(
      switchMap(confirmed => confirmed ? this.api.remove(source) : EMPTY), finalize(() => this.removing = false),
      takeUntil(this.tenant.sessionChanged$)).subscribe({
      // Remove exists only on the report's own page, so a removed report returns to Overview.
      next: () => { this.hiddenPublished = this.hiddenPublished.filter(item => item.id !== source.id); this.removing = false; this.selectedReport = undefined; void this.router.navigate(['/overview'], { queryParams: this.reportingParams(), replaceUrl: true }); },
      error: () => { this.removing = false; this.analysisError = 'The analysis could not be removed. Refresh before trying again.'; },
    }));
  }
  supportingRows(key: keyof demo.Amounts): StatementRow[] {
    const keys: (keyof demo.Amounts)[] = key === 'profit' ? ['serviceCost', 'overhead'] : key === 'netIncome' ? ['interest', 'tax'] : [];
    return this.statements[0]?.rows.filter(row => keys.includes(row.key)) ?? [];
  }
  get comparisonLabel(): string { return this.comparison === 'year' ? 'prior year' : 'prior period'; }
  moduleTitle(module: View): string { return this.moduleTitles[module]; }
  get operatingMargin(): number { return this.ratio(this.snapshot.current.profit, this.snapshot.current.revenue); }
  get marginChange(): number { return this.operatingMargin - this.ratio(this.snapshot.compare.profit, this.snapshot.compare.revenue); }
  get trendMetricLabel(): string { return this.trendMetrics.find(item => item.key === this.trendMetric)!.label; }
  openSummaryInChat(module: View = 'overview'): void {
    const { period, comparePeriod } = this.snapshot;
    // Keep the evidence in the draft even when the visible insight does not repeat the chart values.
    const rows = module === 'overview' ? this.statements.flatMap(section => section.rows.filter(row => row.headline))
      : module === 'profit' ? this.statements[0].rows : module === 'cash' ? this.statements[1].rows
      : module === 'position' ? this.balances : [];
    const evidence = rows.map(row => `${row.label}: ${this.money(row.current)}; comparison ${this.money(row.prior)}.`);
    if (module === 'revenue') evidence.push(`${this.trendMetricLabel}: ${this.money(this.snapshot.current[this.trendMetric])}; comparison ${this.money(this.snapshot.compare[this.trendMetric])}.`);
    const draft = [
      `Business Insight: ${this.companyProfile.profile().name}`,
      `Reporting period: ${period.label} (${period.start} — ${period.end})`,
      `Compared with: ${comparePeriod.label} (${comparePeriod.start} — ${comparePeriod.end})`,
      `Module: ${this.moduleTitles[module]}`,
      ...(module === 'revenue' ? [`Metric: ${this.trendMetricLabel}`] : []),
      `Report ID: ${this.report!.id}; source: ${this.report!.source}; series version: ${this.report!.version}; comparison: ${this.comparison}; USD thousands. Reload with get_overview_report using this comparison before answering.`,
      `Source: ${this.router.url}`,
      '', 'Read the report and analyze the selected data before answering. Explain relevant assumptions with your conclusion.', ...evidence, '', 'My question: ',
    ].join('\n');
    // Reopening an untouched summary draft should not create another conversation.
    if (this.conversation.draft === draft) this.conversation.selectConversation(this.conversation.activeConversation.id);
    else {
      this.conversation.newConversation();
      this.conversation.draft = draft;
      this.conversation.renameConversation(this.conversation.activeConversation.id, `${this.companyProfile.profile().name} · ${period.label}${module === 'overview' ? '' : ' · ' + this.moduleTitles[module]}`);
    }
    this.conversation.open();
  }
  get title(): string {
    return this.view === 'overview' ? 'Business Insight' : overviewDetailTitles[this.view];
  }
  /** The page heading: the company on Overview, the report's own title on a report page, otherwise the detail name. */
  get pageTitle(): string {
    if (this.view === 'overview') return this.companyProfile.profile().name || 'Business Insight';
    return this.view === 'analysis' ? this.selectedReport?.title ?? 'Report' : this.title;
  }
  ngOnInit(): void {
    this.bodyObserver.observe(this.body.nativeElement);
    this.subscriptions.add(this.lookups.reportingIntervals().subscribe(items => {
      this.intervalOptions = items.map(item => ({ value: item.code, label: item.label }));
      this.buildReportFilterFields();
    }));
    this.subscriptions.add(this.companyProfile.load().subscribe({ error: () => { this.profileError = 'Company information could not be loaded.'; } }));
    this.subscriptions.add(this.context.registerView({
      key: 'company-overview', name: 'Business Insight',
      read: () => ({ scrollTop: this.body.nativeElement.scrollTop }),
      describe: () => this.report ? `Business Insight report ${this.report.id}, source ${this.report.source}, series version ${this.report.version}. ${this.title}.${this.selectedReport ? ` Analysis ${this.selectedReport.id}, revision ${this.selectedReport.revision}.` : ''} ${this.snapshot.period.start} to ${this.snapshot.period.end}; comparison ${this.snapshot.comparePeriod.start} to ${this.snapshot.comparePeriod.end}.` : 'Business Insight financial data is unavailable or loading.',
      restore: value => {
        if (typeof value?.scrollTop === 'number' && Number.isFinite(value.scrollTop)) { this.pendingScroll = Math.max(0, value.scrollTop); this.restoreScroll(); }
      },
    }));
    this.subscriptions.add(this.router.events.pipe(filter(event => event instanceof NavigationEnd), startWith(null)).subscribe(() => {
      this.pendingScroll = undefined; clearTimeout(this.scrollTimer);
      const params = this.route.snapshot.queryParamMap;
      const oldPath = `${this.view}/${this.selectedReport?.id ?? ''}`;
      const interval = params.get('interval');
      this.interval = (interval || demo.defaultInterval) as demo.Interval;
      this.start = params.get('period') ?? '';
      this.comparison = params.get('compare') === 'previous' ? 'previous' : 'year';
      this.trendMetric = this.trendMetrics.find(item => item.key === params.get('metric'))?.key ?? 'revenue';
      const segment = this.route.snapshot.firstChild?.url[0]?.path;
      const view = segment === 'reports' ? 'analysis' : segment;
      this.view = view && Object.prototype.hasOwnProperty.call(overviewDetailTitles, view) ? view as View : 'overview';
      this.loadReport(false);
      if (this.initialized && oldPath !== `${this.view}/${this.route.snapshot.firstChild?.paramMap?.get('analysisId') ?? ''}`) {
        this.body.nativeElement.scrollTop = 0;
        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => this.body.nativeElement.focus({ preventScroll: true }));
      }
      this.initialized = true;
    }));
    // A publish tool result in any conversation refreshes the list; no polling or prose parsing.
    // If the reloaded list does not show it while filters or a search are active, a notice keeps it reachable.
    this.subscriptions.add(this.conversation.overviewAnalysisPublished$.subscribe(published => {
      if (this.view !== 'overview' || !this.reportList) return;
      this.pendingPublished = [...this.pendingPublished.filter(item => item.id !== published.id), published];
      // A refresh only re-reads loaded pages; while the first page is still loading or failed, start over instead.
      if (this.reportList.loading || !this.reportList.items.length) this.reportList.reload(); else this.reportList.refresh();
    }));
    this.subscriptions.add(this.tenant.sessionChanged$.subscribe(() => {
      this.findingType = undefined; this.hiddenPublished = []; this.pendingPublished = []; this.reportListFilters = {}; this.reportSearch = '';
      this.periodFilterRequest?.unsubscribe(); this.periodFilterOptions = []; this.buildReportFilterFields();
      this.loadSubscription?.unsubscribe(); this.report = undefined; this.selectedReport = undefined;
      this.removing = false; this.companyLocationError = ''; this.pendingScroll = undefined; this.profileError = ''; this.loading = true;
      if (this.tenant.current) {
        this.initialized = false;
        this.subscriptions.add(this.companyProfile.load().subscribe({ error: () => { this.profileError = 'Company information could not be loaded.'; } }));
        const sessionVersion = this.tenant.sessionVersion;
        void this.router.navigate(['/overview'], { queryParams: {}, replaceUrl: true }).then(changed => {
          if (!changed && this.router.url === '/overview' && this.tenant.sessionVersion === sessionVersion) {
            this.view = 'overview'; this.interval = demo.defaultInterval; this.start = ''; this.comparison = 'year'; this.reportList?.refresh(); this.loadReport();
          }
        });
      }
    }));
  }

  ngOnDestroy(): void { this.discussionRequest?.unsubscribe(); this.subscriptions.unsubscribe(); this.periodFilterRequest?.unsubscribe(); this.loadSubscription?.unsubscribe(); this.previewObserver.disconnect(); this.bodyObserver.disconnect(); clearTimeout(this.focusTimer); clearTimeout(this.scrollTimer); }

  private restoreScroll(): void {
    if (this.loading || this.pendingScroll === undefined) return;
    clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(() => { this.body.nativeElement.scrollTop = this.pendingScroll ?? 0; this.pendingScroll = undefined; });
  }
  private reportingParams(): Params {
    // The shared data list restores its own filters, search and sort by context key when Overview returns.
    return { ...rootBreadcrumbQueryParams(this.route.snapshot), interval: this.interval, period: this.start, compare: this.comparison };
  }
  change(values: Params, rememberSource = false): void {
    const { view: requestedView = this.view, ...queryParams } = values;
    const reportChanged = 'interval' in values || 'period' in values || 'compare' in values;
    const view = requestedView === 'analysis' && reportChanged ? 'overview' : requestedView;
    void this.router.navigate(view && view !== 'overview' ? ['/overview', view] : ['/overview'], {
      queryParams: { ...this.reportingParams(), ...queryParams, view: null }, queryParamsHandling: 'merge', preserveFragment: true,
      state: rememberSource ? this.navigation.sourceState(this.view === 'overview' ? 'Business Insight' : this.title)
        : view === this.view ? this.navigation.returnState : {},
    });
  }
  get backLabel(): string {
    return `Back to ${this.navigation.returnLabel ?? 'Business Insight'}`;
  }
  back(): void {
    if (this.navigation.returnToSource()) return;
    this.open('overview');
  }
  changeInterval(value: demo.Interval): void { this.change({ interval: value, period: null, view: this.view === 'analysis' ? 'overview' : this.view }); }
  open(view: View, metric?: TrendMetric): void {
    if (view === 'overview') {
      void this.router.navigate(['/overview'], { queryParams: this.reportingParams() });
      return;
    }
    this.change({ view, ...(metric ? { metric } : {}) }, true);
  }
  selectTrend(event: { active?: object[] }, detail: View = 'revenue'): void {
    const hit = (event.active as ActiveElement[] | undefined)?.find(item => item.datasetIndex === 0);
    if (hit && this.trendPeriods[hit.index]) this.change({ view: this.view === 'overview' ? detail : this.view, period: this.trendPeriods[hit.index].start }, this.view === 'overview');
  }
  private refresh(): void {
    const { current: a, compare: b } = this.snapshot;
    this.statements = [
      { name: 'Income statement summary', view: 'profit' as View, lines: incomeLines },
      { name: 'Cash flow summary', view: 'cash' as View, lines: cashLines },
    ].map(section => ({ name: section.name, view: section.view,
      rows: section.lines.map(line => ({ ...line, current: a[line.key], prior: b[line.key],
        margin: line.key === 'profit' || line.key === 'netIncome'
          ? { current: this.ratio(a[line.key], a.revenue), prior: this.ratio(b[line.key], b.revenue) } : undefined })),
    }));
    this.balances = ([
      ['cash', 'Cash balance'], ['receivables', 'Accounts receivable'],
      ['payables', 'Accounts payable'], ['debt', 'Debt'],
    ] as const).map(([key, label]) => ({ label, current: this.snapshot.balance[key], prior: this.snapshot.compareBalance[key] }));
    this.details = this.statements.find(section => section.view === this.view)?.rows ?? [this.statements[0].rows[0]];
    if (this.view === 'position') this.details = this.balances;
    this.detailOptions = { responsive: true, maintainAspectRatio: false, animation: false, indexAxis: 'y',
      plugins: { legend: { position: 'bottom', labels: { color: this.color('text'), usePointStyle: true, boxWidth: 8, boxHeight: 8 } }, tooltip: { callbacks: { label: item => item.parsed.x == null ? '' : this.money(item.parsed.x) } } },
      scales: { x: { ticks: { color: this.color('muted'), maxRotation: 0, maxTicksLimit: this.view === 'overview' ? 4 : undefined, callback: value => this.money(Number(value)) }, grid: { color: this.color('border-soft') } }, y: { ticks: { color: this.color('text') }, grid: { display: false } } },
      datasets: { bar: { borderRadius: 2, maxBarThickness: this.view === 'overview' ? 14 : 28 } },
    };
    this.buildTrend();
    this.cashChart = { labels: cashLines.slice(0, 3).map(row => row.label), datasets: [
      { label: this.snapshot.period.label, data: [a.operating, a.investing, a.financing], backgroundColor: this.color('primary') },
      { label: this.snapshot.comparePeriod.label, data: [b.operating, b.investing, b.financing], backgroundColor: this.color('secondary') },
    ] };
  }
  private buildTrend(): void {
    const history = this.report!.history.map(item => item.snapshot.period);
    this.trendPeriods = history;
    const actual = this.report!.history.map(item => item.snapshot);
    const labels = history.map(p => p.label);
    const datasets: ChartData<'line'>['datasets'] = [
      { label: 'Actual', data: actual.map(s => s.current[this.trendMetric]), borderColor: this.color('primary'), backgroundColor: this.color('primary'), pointBackgroundColor: this.color('primary'), pointBorderColor: this.color('primary'), pointRadius: 3, borderWidth: 2 },
      { label: this.comparisonLabel, data: actual.map(s => s.compare[this.trendMetric]), borderColor: this.color('secondary'), backgroundColor: this.color('secondary'), pointBackgroundColor: this.color('secondary'), pointBorderColor: this.color('secondary'), pointRadius: 0, borderWidth: 2, borderDash: [6, 4] },
    ];
    this.trend = { labels, datasets };
    this.trendDescription = actual.map(s => `${s.period.label}: ${this.money(s.current[this.trendMetric])}, comparison ${this.money(s.compare[this.trendMetric])}`).join('. ');
    this.trendOptions = {
      responsive: true, maintainAspectRatio: false, animation: false,
      plugins: { legend: { position: 'bottom', labels: { color: this.color('text'), usePointStyle: true, boxWidth: 20, boxHeight: 8, pointStyleWidth: 20, generateLabels: this.lineLegend, sort: (a, b) => (a.datasetIndex ?? 0) - (b.datasetIndex ?? 0) } },
        tooltip: { callbacks: { label: item => item.parsed.y == null ? '' : `${item.dataset.label}: ${this.money(item.parsed.y)}` } } },
      scales: { x: { grid: { display: false }, ticks: { color: this.color('muted'), maxRotation: 0 } },
        y: { beginAtZero: true, ticks: { color: this.color('muted'), callback: value => this.money(Number(value)) }, grid: { color: this.color('border-soft') } } },
    };
    this.profitTrend = { labels: history.map(p => p.label), datasets: [
      { label: 'Operating margin', data: actual.map(s => s.current.revenue > 0 ? this.ratio(s.current.profit, s.current.revenue) : null), borderColor: this.color('primary'), pointBackgroundColor: this.color('primary'), pointBorderColor: this.color('primary'), backgroundColor: this.color('primary'), borderWidth: 2 },
      { label: this.comparisonLabel, data: actual.map(s => s.compare.revenue > 0 ? this.ratio(s.compare.profit, s.compare.revenue) : null), borderColor: this.color('secondary'), backgroundColor: this.color('secondary'), pointBackgroundColor: this.color('secondary'), pointBorderColor: this.color('secondary'), pointRadius: 0, borderWidth: 2, borderDash: [6, 4] },
    ] };
    // Margins move by tenths of a point; a zero-based axis flattens them, so fit the data and disclose the range.
    const margins = actual.flatMap(s => [s.current, s.compare]).map(m => 100 * m.profit / m.revenue).filter(Number.isFinite);
    const marginMin = margins.length ? Math.floor(Math.min(...margins) - 1) : undefined;
    const marginMax = margins.length ? Math.ceil(Math.max(...margins) + 1) : undefined;
    this.marginAxisNote = marginMin !== undefined && marginMax !== undefined && (marginMin > 0 || marginMax < 0)
      ? `Margin axis ${marginMin}% to ${marginMax}%, not from zero.` : '';
    this.profitTrendOptions = { responsive: true, maintainAspectRatio: false, animation: false,
      plugins: { legend: { position: 'bottom', labels: { color: this.color('text'), usePointStyle: true, boxWidth: 20, boxHeight: 8, pointStyleWidth: 20, generateLabels: this.lineLegend } }, tooltip: { callbacks: { label: item => `${item.dataset.label}: ${item.parsed.y?.toFixed(1)}%` } } },
      scales: { x: { ticks: { color: this.color('muted'), maxRotation: 0 }, grid: { display: false } }, y: { min: marginMin, max: marginMax, ticks: { color: this.color('muted'), callback: value => value + '%' }, grid: { color: this.color('border-soft') } } },
    };
  }
}
